import PageHeader from "@/components/PageHeader";
import Seo from "@/components/Seo";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display font-bold text-navy-900 text-lg">{title}</h2>
    <div className="mt-3 space-y-3 text-sm lg:text-base text-slateSoft leading-relaxed">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo
        title="Privacy Policy | WHO — Warm Hand Off"
        description="How Warm Hand Off LLC collects, uses, and protects information submitted through warmhandoffhq.com."
        path="/privacy"
      />
      <PageHeader
        eyebrow="Privacy Policy"
        title="Your information, handled with care."
        lede="How Warm Hand Off LLC collects, uses, and protects information submitted through this website."
      />
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-14 lg:py-16">
        <div className="max-w-3xl">
          <p className="text-sm text-slateSoft" data-testid="privacy-effective-date">
            <strong className="text-navy-900">Effective date:</strong> October 17, 2026
          </p>
          <p className="mt-6 text-sm lg:text-base text-slateSoft leading-relaxed">
            Warm Hand Off LLC (&ldquo;WHO,&rdquo; &ldquo;we,&rdquo; &ldquo;our&rdquo;) operates warmhandoffhq.com
            as a public, informational website. This policy explains what information this website collects,
            how it is used, and the choices you have.
          </p>
          <p className="mt-4 text-sm lg:text-base text-slateSoft leading-relaxed">
            <strong className="text-navy-900">Scope: this website is informational only.</strong> WHO is building
            a coordinated service platform for veterans and their communities. That platform is a separate,
            future capability and is not operational through this website. This website does not provide case
            management, referrals, or veteran services, and it does not collect service records, medical
            information, or case data. Please do not submit medical, benefits, or other sensitive personal
            information through this website.
          </p>

          <Section title="1. Information we collect">
            <p>
              <strong className="text-navy-900">Contact form.</strong> If you submit our contact form, we collect
              the information you choose to provide: your name, organization, title, email address, phone number,
              organization type, whether you are interested in pilot partnership, your message, and the date and
              time of submission.
            </p>
            <p>
              <strong className="text-navy-900">WHO Guides&trade; demonstration.</strong> This website offers a
              public, clearly labeled demonstration of WHO Guides for marketing purposes. It is not an operational
              service. Do not enter personal, medical, or crisis information into the demonstration. Demonstration
              messages are processed transiently by our managed AI service provider solely to generate the
              demonstration response; WHO does not store demonstration conversation content in its own systems.
              Only technical metadata (such as time of use and message length) is retained by WHO. If you are a
              veteran in crisis, contact the Veterans Crisis Line by dialing 988 and pressing 1.
            </p>
            <p>
              <strong className="text-navy-900">Public Discovery directory.</strong> The directory contains publicly
              available business and organizational information about veteran-serving organizations, gathered from
              public sources. It is provided for informational discovery only. Inclusion in the directory does not
              constitute a partnership, endorsement, referral agreement, or participation in WHO&rsquo;s coordinated
              services by the listed organization.
            </p>
          </Section>

          <Section title="2. How we use information">
            <p>
              Contact-form submissions are used solely to respond to your inquiry and to manage WHO&rsquo;s
              pilot-partnership outreach. We do not sell personal information, and we do not use contact-form
              submissions for advertising.
            </p>
          </Section>

          <Section title="3. How inquiries are stored and notifications">
            <p>
              Submissions are stored in a secure, access-controlled administrative inbox hosted with our website
              platform provider. When you submit the form, the information you provide is processed by our
              authorized website hosting provider for storage and by our transactional email service provider to
              deliver a notification — containing your submission details — to WHO at marlon@warmhandoffhq.com.
              Access to stored inquiries is limited to authorized WHO administrators.
            </p>
          </Section>

          <Section title="4. Analytics and cookies">
            <p>
              This website uses Plausible Analytics, a privacy-friendly measurement tool that does not use cookies
              and does not collect personal data or track visitors across websites. It provides WHO with aggregate,
              anonymous statistics such as page-view counts. We do not use advertising cookies or third-party
              tracking pixels.
            </p>
          </Section>

          <Section title="5. Service providers">
            <p>
              This website is hosted and operated using Emergent (application hosting, database, and transactional
              email delivery). Standard content-delivery services (such as web font providers) may process technical
              request data (such as IP addresses) as a normal part of delivering web pages. The WHO Guides
              demonstration processes demonstration messages through Emergent&rsquo;s managed AI infrastructure
              solely to generate demonstration responses.
            </p>
          </Section>

          <Section title="6. Data retention and your choices">
            <p>
              We retain contact-form submissions for as long as needed to respond to your inquiry and manage our
              relationship with you. You may request a copy, correction, or deletion of your submitted information
              at any time by emailing marlon@warmhandoffhq.com. We will honor deletion requests within a reasonable
              period, subject to applicable legal, security, and legitimate operational retention requirements
              (for example, records we must keep for security auditing or legal compliance).
            </p>
          </Section>

          <Section title="7. Security">
            <p>
              We apply reasonable administrative and technical measures appropriate to an informational website,
              including access-controlled administrative systems, authenticated administrator access, and encrypted
              transmission (HTTPS). No method of transmission or storage is completely secure, and we cannot
              guarantee absolute security.
            </p>
          </Section>

          <Section title="8. Children&rsquo;s privacy">
            <p>
              This website is intended for adults and organizations. We do not knowingly collect personal
              information from children under 13.
            </p>
          </Section>

          <Section title="9. Changes to this policy">
            <p>
              If we update this policy, the revised version will be posted on this page with an updated
              effective date.
            </p>
          </Section>

          <Section title="10. Contact">
            <p>
              Warm Hand Off LLC — Pittsburgh, Pennsylvania
              <br />
              Email:{" "}
              <a href="mailto:marlon@warmhandoffhq.com" className="text-navy-900 underline hover:text-gold-deep" data-testid="privacy-contact-email">
                marlon@warmhandoffhq.com
              </a>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
